import { Context } from './context'
import { Logger } from './logger'
import { ObservableValue, createObservableValue } from './observable'
import { fetchWithTimeout } from './tools'

export enum ConnectionStatus {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
}

export interface ConnectionStatusService {
  networkStatus: ObservableValue<ConnectionStatus, Context>
}

export class MockedConnectionStatusService implements ConnectionStatusService {
  networkStatus = createObservableValue<ConnectionStatus, Context>(ConnectionStatus.OFFLINE)
}

export class ConnectionStatusServiceImpl implements ConnectionStatusService {
  networkStatus = createObservableValue<ConnectionStatus, Context>(ConnectionStatus.OFFLINE)

  private logger = new Logger('ConnectionStatusServiceImpl')
  private connectionError: string | undefined

  constructor(
    private healthCheckUrl: string | (() => Promise<string>),
    periodMs?: number,
    private checkTimeoutMs: number = 1000,
  ) {
    this.logger.debug({}, '<4756c1e4> Initialize', { healthCheckUrl, periodMs })
    setInterval(() => this.checkConnectionStatus(), periodMs ?? 10000)
    this.checkConnectionStatus().catch(e =>
      this.logger.error({}, '<8f3e1a16> Error during connection check', { error: e }),
    )
  }

  private async checkConnectionStatus() {
    this.logger.debug({}, '<76a6725a> Check connection')
    let isOk: boolean
    const lastError = this.connectionError
    try {
      let url: string
      if (typeof this.healthCheckUrl === 'string') {
        url = this.healthCheckUrl
      } else {
        url = await this.healthCheckUrl()
      }
      const response = await fetchWithTimeout(url, { method: 'GET', timeoutMs: this.checkTimeoutMs })
      isOk = response.ok
      if (!response.ok) this.connectionError = `Request status: ${response.statusText}`
    } catch (e) {
      this.connectionError = e instanceof Error ? e.message : 'unknown'
      isOk = false
    }
    if (lastError !== this.connectionError && this.connectionError)
      this.logger.error({}, `<e69c36e3> Current connection error is: ${this.connectionError}`)
    const prevStatus = this.networkStatus.value
    this.networkStatus.setValue(isOk ? ConnectionStatus.ONLINE : ConnectionStatus.OFFLINE)
    if (this.networkStatus.value !== prevStatus)
      this.logger.debug({}, '<88c8612e> Connection status changed', { status: this.networkStatus.value })
  }
}
